import qs from 'qs';

export const toArray = (value: string | string[]) => {
  if (value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

export const extractFromArray = (
  value: string | object | (string | object)[] | undefined,
  index = 0,
) => {
  if (value === undefined) {
    return undefined;
  }
  return Array.isArray(value) ? value[index] : value;
};

export const roundNumber = (value: number, precision = 2): number => {
  return Number(Math.round((value + 'e+' + precision) as any) + 'e-' + precision);
};

export const parseQuery = (search) => {
  return qs.parse(search, {
    ignoreQueryPrefix: true,
    strictNullHandling: true,
    arrayLimit: 1000,
  });
};

export const stringifyQuery = (search) => {
  return qs.stringify(search, {
    encodeValuesOnly: true,
    strictNullHandling: true,
  });
};

export const fieldName = (string: string, prefix?: string) => {
  if (!prefix) {
    return string;
  }
  return `${prefix}.${string}`;
};

export const pluralize = (count: number, noun: string, suffix = 's') => {
  const has_suffix = noun.indexOf(suffix, noun.length - suffix.length) !== -1;

  if (count <= 1) {
    if (has_suffix) {
      return noun.slice(0, -suffix.length);
    }
    return noun;
  }
  if (!has_suffix) {
    return `${noun}${suffix}`;
  }
  return noun;
};

export const generateUEID = (): string => {
  const first = (Math.random() * 46656) | 0;
  const second = (Math.random() * 46656) | 0;
  return ('000' + first.toString(36)).slice(-3) + ('000' + second.toString(36)).slice(-3);
};

export const numberWithCommas = (x: number | null | undefined) => {
  if (x === null || x === undefined || isNaN(x)) {
    return '0';
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatNumberShorthand = (num: number): string => {
  if (num < 1000) {
    return num.toString();
  }

  const units = ['', 'k', 'm', 'b', 't'];
  let unitIndex = 0;
  let value = num;

  // Find the appropriate unit
  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }

  // Format to have exactly 3 significant digits
  let formatted: string;
  if (value >= 100) {
    // 100-999 range: show as integer (e.g., 105k)
    formatted = Math.round(value).toString();
  } else if (value >= 10) {
    // 10-99 range: show 1 decimal place (e.g., 10.5k)
    formatted = value.toFixed(1);
  } else {
    // 1-9 range: show 2 decimal places (e.g., 1.05k)
    formatted = value.toFixed(2);
  }

  // Remove trailing zeros from decimal places only (not from whole numbers)
  if (formatted.includes('.')) {
    formatted = formatted.replace(/\.?0+$/, '');
  }

  return `${formatted}${units[unitIndex]}`;
};

export const formatAmountOfTime = (minutes: number): string => {
  if (minutes === 1) {
    return `${minutes} minute`;
  }
  if (minutes < 120) {
    return `${minutes} minutes`;
  }
  if (minutes === 120) {
    return `2 hours`;
  }
  if (minutes >= 120) {
    const hours = Math.floor(minutes / 60);
    const remaining_minutes = minutes - hours * 60;
    if (minutes > 0) {
      return `${hours}h ${remaining_minutes} min`;
    }
    return `${hours} hours`;
  }
  return minutes.toString();
};

export const isValidUrl = (url: string) => {
  // Check before even instantiating `URL` because it can otheriwse
  // allow `http:something` to be parsed as valid.
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false;
  }

  // The URL constructor encodes spaces instead of throwing an error
  // but our backend uses Joi and will reject it.
  if (url.trim().includes(' ')) {
    return false;
  }

  try {
    const parsed = new URL(url);

    // Validate hostname: only allow letters, digits, hyphens, dots, and
    // IPv6 brackets. Underscores and special symbols are not valid in
    // hostnames used for HTTP destinations.
    const hostname = parsed.hostname;
    if (!hostname) {
      return false;
    }
    // Allow IPv6 addresses (brackets and colons) and regular hostnames
    // (letters, digits, hyphens, dots only)
    if (!/^[a-zA-Z0-9.\-:\[\]]+$/.test(hostname)) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string || typeof string !== 'string') return string;

  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Validates that a redirect path is safe to use (internal path only).
 * Prevents open redirect vulnerabilities by ensuring only relative paths are allowed.
 *
 * Modern browsers provide built-in protection via pushState() for most threats,
 * but this validation provides defense in depth and catches edge cases.
 *
 * @param redirect_path - The redirect path from query parameter or user input
 * @returns true if the path is safe (internal), false otherwise
 */
export const isInternalRedirectPath = (redirect_path: string): boolean => {
  if (!redirect_path || typeof redirect_path !== 'string') {
    return false;
  }

  const trimmed = redirect_path.trim();

  // Reject empty strings
  if (trimmed.length === 0) {
    return false;
  }

  // Must start with a forward slash (relative path)
  if (!trimmed.startsWith('/')) {
    return false;
  }

  // Reject protocol-relative URLs (//evil.com, and /\evil.com which browsers read the same way)
  if (trimmed.startsWith('//') || trimmed.startsWith('/\\')) {
    return false;
  }

  // Block any colons in the path - catches all protocol separators
  // Examples: https://evil.com, /https:/evil.com, javascript:alert(1), data:text/html
  if (trimmed.includes(':')) {
    return false;
  }

  // Browsers drop tabs and newlines and read "\" as "/" when resolving a URL, so
  // "/\t/evil.com" still lands on evil.com. Make sure the path stays on this origin.
  const base_url = 'https://dashboard.invalid';
  try {
    return new URL(trimmed, base_url).origin === base_url;
  } catch (e) {
    return false;
  }
};

/**
 * Deep merge two objects, recursively merging nested objects
 * Arrays and primitive values from obj2 will overwrite obj1
 */
export const deepMergeFilters = (obj1: any, obj2: any): any => {
  const result = { ...obj1 };
  for (const key in obj2) {
    if (obj2[key] && typeof obj2[key] === 'object' && !Array.isArray(obj2[key])) {
      result[key] = deepMergeFilters(result[key] || {}, obj2[key]);
    } else {
      result[key] = obj2[key];
    }
  }
  return result;
};
